import React from 'react';
import { formatSingleAddress, getAddressRule } from '@repo/checkout/lib/addressRules/index';

// Renders an address using the canonical per-country formatting rules from the
// checkout package. `customer.address` / `customer.shipping.address` use the
// legacy `state` key while the structured address field uses `region` — we
// normalize before delegating.
export function getAddressLines(address) {
  if (!address) return [];
  const normalized = { ...address, region: address.region ?? address.state };
  const country = normalized.country;
  if (!country) {
    return [
      normalized.line1,
      normalized.line2,
      normalized.city,
      normalized.region,
      normalized.postalCode,
    ].filter(Boolean);
  }
  return formatSingleAddress(normalized, getAddressRule(country)).split('\n').filter(Boolean);
}

function renderLines(address) {
  if (!address) return null;
  return getAddressLines(address).map((p, i) => <div key={i}>{p}</div>);
}

export function AddressLines({ address }) {
  if (!address) return null;
  return <div>{renderLines(address)}</div>;
}

const ADDRESS_COMPARE_KEYS = ['line1', 'line2', 'city', 'region', 'postalCode', 'country'];

function addressesEqual(a, b) {
  if (!a || !b) return false;
  return ADDRESS_COMPARE_KEYS.every(k => (a[k] ?? undefined) === (b[k] ?? undefined));
}

// Returns one or two rows describing the structured address — one row per
// distinct address. The heading uses the field's stored label (e.g. "Address")
// and the subtitle declares whether each row is billing, shipping, or both.
export function getAddressRows(fields) {
  const entry = fields?.find(f => f?.element === 'address');
  const meta = entry?.meta;
  if (!meta) return [];

  const heading = entry.label;

  if (!meta.shipping) {
    if (!meta.billing) return [];
    return [{ heading, subtitle: 'Billing address', address: meta.billing }];
  }

  if (meta.billing && addressesEqual(meta.billing, meta.shipping)) {
    return [{ heading, subtitle: 'Billing & shipping address', address: meta.shipping }];
  }

  const rows = [{ heading, subtitle: 'Shipping address', address: meta.shipping }];
  if (meta.billing) {
    rows.push({ heading, subtitle: 'Billing address', address: meta.billing });
  }
  return rows;
}
